<template>
<teleport to="body">
    <div class="modal-content">
        <h5 class="modal-title mx-2 my-3">Select Contact to Share File</h5>
        <div class="align-items-center">
            <div class="text-right col-12 d-flex flex-column justify-content-center">
                <div class="row">
                    <div class="col-12">
                        <input id="search" v-model="searchQuery" placeholder="Search here" class="contact-field border-radius-lg text-start" style="width: 90%;" />
                    </div>

                    <div class="col-12 contact-list-scroll">
                        <template v-if="filteredContacts.length">
                            <ul class="list-group">
                                <li 
                                    v-for="contact in filteredContacts" 
                                    :key="contact.email" 
                                    class="py-3 my-1 border-0 list-group-item d-flex align-items-center"
                                    :class="{ 
                                        'selected-contact': selectedContacts.includes(contact),
                                        'contact-already-shared': isContactPermitted(contact),
                                        'contact-unverified': !contact.publicKey
                                    }"
                                    @click="() => canSelectContact(contact) && toggleSelection(contact)"
                                >
                                    <!-- Avatar -->
                                    <div class="pa-2">
                                        <material-avatar
                                            img=""
                                            :alt="contact.name"
                                            :color="contact.color"
                                            size="sm"
                                            shadow="sm"
                                            circular
                                        />
                                    </div>

                                    <!-- Contact Info -->
                                    <div class="d-flex align-items-start flex-column justify-content-end mx-2 flex-grow-1">
                                        <div class="d-flex align-items-center gap-2">
                                            <h6 class="mb-0 text-sm">{{ contact.name }}</h6>
                                            <span v-if="!contact.publicKey" class="badge bg-shareguard text-white" style="font-size: 0.7rem;">
                                                Unverified
                                            </span>
                                        </div>
                                        <p class="mb-0 text-xs">{{ contact.email }}</p>
                                    </div>

                                    <!-- Action Button (Checkbox or Revoke) -->
                                    <div class="d-flex align-items-end">
                                        <template v-if="isContactPermitted(contact)">
                                            <button 
                                                class="bg-secondary btn btn-sm text-white mb-0 " 
                                                :disabled="isRevoking"
                                                @click.stop="revokeAccess(contact)"
                                            >
                                                <span v-if="isRevokingFor(contact)" class="spinner-border spinner-border-sm me-1" role="status"></span>
                                                REVOKE ACCESS
                                            </button>
                                        </template>
                                        <template v-else>
                                            <input 
                                                type="checkbox" 
                                                :checked="selectedContacts.includes(contact)"
                                                :disabled="!contact.publicKey"
                                                class="mb-0" 
                                                @click.stop="canSelectContact(contact) && toggleSelection(contact)"
                                            />
                                        </template>
                                    </div>
                                </li>
                            </ul>
                        </template>
                        <template v-else>
                            <div class="no-contacts-message text-center text-muted py-4">
                                No contacts found matching your search.
                            </div>
                        </template>
                    </div>

                    <!-- Share Button -->
                    <div class="col-12 mt-3">
                        <div class="d-flex justify-content-end">
                            <button class="btn btn-outline-shareguard me-2" @click="closeModal">Close</button>
                            <button 
                                class="btn btn-shareguard" 
                                :disabled="!selectedContacts.length || isSharing" 
                                @click="shareFiles"
                            >
                                <span v-if="isSharing" class="spinner-border spinner-border-sm me-1" role="status"></span>
                                {{ isSharing ? "Sharing..." : "Share" }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</teleport>
</template>

<script>
import { mapState, mapActions } from "vuex";
import MaterialAvatar from '@/components/ui/MaterialAvatar';

export default {
    name: 'SelectContactModal',
    components: {
        MaterialAvatar
    },
    props: {
        fileId: {
            type: String,
            default: "",
        },
        permittedViewers: {
            type: Array,
            default: () => []
        }
    },
    emits: ['close-modal', 'access-revoked', 'contact-picked', 'contacts-picked'],
    data() {
        return {
            searchQuery: '',
            selectedContacts: [],
            isSharing: false,
            isRevoking: false,
            revokingContacts: new Set(),
            localPermittedViewers: []
        };
    },
    computed: {
        ...mapState("contacts", ["contactList"]),
        filteredContacts() {
            if (!this.searchQuery) return this.contactList;
            
            return this.contactList.filter(contact =>
                contact.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                contact.email.toLowerCase().includes(this.searchQuery.toLowerCase())
            );
        }
    },
    watch: {
        permittedViewers: {
            handler(newValue) {
                this.localPermittedViewers = [...newValue];
            },
            immediate: true
        }
    },
    created() {
        this.$store.commit('setOverlay', true);
    },
    async mounted() {
        await this.fetchContactList();
        this.localPermittedViewers = [...this.permittedViewers];
    },
    beforeUnmount() {
        this.$store.commit('setOverlay', false);
    },
    methods: {
        ...mapActions("contacts", [
            "fetchContactList", 
            // "addFileIdToContactAction", 
            "removeFileIdFromContactAction"
        ]),
        ...mapActions("snackbar", ["showSnackbar"]),
        
        isContactPermitted(contact) {
            return this.localPermittedViewers.some(viewer => viewer.email === contact.email);
        },

        isRevokingFor(contact) {
            return this.revokingContacts.has(contact.email);
        },
        
        canSelectContact(contact) {
            return !this.isContactPermitted(contact) && contact.publicKey;
        },

        toggleSelection(contact) {
            if (!this.canSelectContact(contact)) return;
            
            if (this.selectedContacts.includes(contact)) {
                this.selectedContacts = this.selectedContacts.filter(
                    selectedContact => selectedContact.email !== contact.email
                );
            } else {
                this.selectedContacts.push(contact);
            }
        },

        async revokeAccess(contact) {
            if (this.isRevoking) return;
            
            this.revokingContacts.add(contact.email);
            try {
                await this.removeFileIdFromContactAction({
                    contact: contact,
                    fileId: this.fileId
                });
                this.localPermittedViewers = this.localPermittedViewers.filter(
                    viewer => viewer.email !== contact.email
                );
                this.showSnackbar({ 
                    title: `Access revoked for ${contact.name}`, 
                    color: "success" 
                });
                this.$emit('access-revoked', contact);
            } catch (error) {
                console.error("Error revoking access:", error);
                this.showSnackbar({ 
                    title: `Failed to revoke access for ${contact.name}`, 
                    color: "danger"
                });
            } finally {
                this.revokingContacts.delete(contact.email);
            }
        },

        async shareFiles() {
            if (this.selectedContacts.length === 0) return;
            this.isSharing = true;
            try {
                this.$emit('contacts-picked', this.selectedContacts);
                this.$emit('close-modal');
            } catch (error) {
                console.error("Error sharing file!", error);
                this.showSnackbar({ 
                    title: "Error selecting contact(s) for sharing!", 
                    color: "danger"
                });
            } finally {
                this.isSharing = false;
            }
        },
        
        closeModal() {
            this.$emit("close-modal");
        }
    }
}
</script>

<style scoped>
.modal-content {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: white;
    padding: 1rem;
    border-radius: 0.5rem;
    width: 95vw;
    max-width: 400px;
    max-height: 90vh;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
}

.contact-field {
    border: 0.5px solid grey;
    padding: 5px 10px;
    margin: 5px;
}

/* Selected Contact Highlight */
.selected-contact {
    background-color: rgba(52, 152, 219, 0.2);
    border-radius: 5px;
}

.contact-already-shared {
    background-color: #f8f9fa;
}

/* Checkbox */
input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
    margin-left: auto;
    margin-right: auto;
    display: block;
}

.badge {
    font-size: 0.75rem;
    font-weight: 500;
}

/* Button Styles */
.btn-outline-danger {
    border-color: #dc3545;
    color: #dc3545;
}

.btn-outline-danger:hover {
    background-color: #dc3545;
    color: white;
}

.btn-outline-danger:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

/* Spinner */
.spinner-border-sm {
    width: 1rem;
    height: 1rem;
    border-width: 0.15em;
}

.btn {
    min-width: 125px;
    padding: 0.5rem 1rem;
}

.contact-list-scroll {
    max-height: 45vh;
    overflow-y: auto;
    margin-bottom: 1rem;
}

@media (max-width: 600px) {
    .modal-content {
        width: 98vw;
        max-width: 98vw;
        padding: 0.5rem;
        max-height: 98vh;
    }
    .contact-list-scroll {
        max-height: 40vh;
    }
}

.contact-unverified {
    opacity: 0.6;
    cursor: not-allowed;
    background-color: #f8f9fa;
}

.contact-unverified:hover {
    background-color: #f8f9fa !important;
}

/* Update checkbox styles for disabled state */
input[type="checkbox"]:disabled {
    cursor: not-allowed;
    opacity: 0.5;
}
</style>
